import { Layout, Menu, Drawer } from 'antd';
import type { MenuProps } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore, useAuthStore, useResponsive, getMenuItems, getMenuKeyFromPath, getParentMenuKey, filterMenuByPermissions } from '@/shared';
import LogoIcon from '@/assets/logo.svg';
import styles from './Sidebar.module.css';

const { Sider } = Layout;

export const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const sidebarCollapsed = useAppStore((state) => state.sidebarCollapsed);
  const mobileSidebarOpen = useAppStore((state) => state.mobileSidebarOpen);
  const setMobileSidebarOpen = useAppStore((state) => state.setMobileSidebarOpen);
  const selectedMenuKey = useAppStore((state) => state.selectedMenuKey);
  const openMenuKeys = useAppStore((state) => state.openMenuKeys);
  const setSelectedMenuKey = useAppStore((state) => state.setSelectedMenuKey);
  const setOpenMenuKeys = useAppStore((state) => state.setOpenMenuKeys);
  
  const permissions = useAuthStore((state) => state.permissions);

  const hasPermission = useCallback((permissionName: string): boolean => {
    const [resource, action] = permissionName.split('.');
    
    if (!resource || !action) {
      return false;
    }
    
    const resourcePermissions = permissions.find((item) => item.name === resource);
    
    if (!resourcePermissions) {
      return false;
    }
    
    return resourcePermissions.permissions[action] === true;
  }, [permissions]);

  const menuItems = useMemo(() => {
    const items = getMenuItems(t);
    return filterMenuByPermissions(items, hasPermission);
  }, [t, hasPermission]);

  useEffect(() => {
    const menuKey = getMenuKeyFromPath(location.pathname, t);
    if (menuKey) {
      setSelectedMenuKey(menuKey);

      const parentKey = getParentMenuKey(menuKey, t);
      if (parentKey) {
        const currentOpenKeys = useAppStore.getState().openMenuKeys;
        if (!currentOpenKeys.includes(parentKey)) {
          setOpenMenuKeys([...currentOpenKeys, parentKey]);
        }
      }
    } else {
      setSelectedMenuKey('');
    }
  }, [location.pathname, t, setSelectedMenuKey, setOpenMenuKeys]);

  const handleMenuSelect: MenuProps['onSelect'] = ({ key }) => {
    setSelectedMenuKey(key);
    if (typeof key === 'string' && key.startsWith('/')) {
      navigate(key);
    }
    if (isMobile) {
      setMobileSidebarOpen(false);
    }
  };

  const handleOpenChange: MenuProps['onOpenChange'] = (keys) => {
    setOpenMenuKeys(keys);
  };

  const sidebarContent = (
    <div className={styles.siderInner}>
      <div className={isMobile ? styles.logo : (sidebarCollapsed ? styles.logoCollapsed : styles.logo)}>
        <img src={LogoIcon} alt='Wasit Admin' className={styles.logoIcon} />
        {(isMobile || !sidebarCollapsed) && <span className={styles.logoText}>WasitAdmin</span>}
      </div>

      <div className={styles.menuContainer}>
        <Menu
          theme='light'
          mode='inline'
          selectedKeys={selectedMenuKey ? [selectedMenuKey] : []}
          openKeys={isMobile ? openMenuKeys : (sidebarCollapsed ? [] : openMenuKeys)}
          onSelect={handleMenuSelect}
          onOpenChange={handleOpenChange}
          items={menuItems}
          className={`${styles.menu} ${isMobile ? styles.menuMobile : ''}`}
        />
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer
        placement='left'
        open={mobileSidebarOpen}
        onClose={() => setMobileSidebarOpen(false)}
        width={290}
        className={styles.mobileDrawer}
        styles={{ body: { padding: 0 }, header: { display: 'none' } }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={sidebarCollapsed}
      width={290}
      collapsedWidth={92}
      className={styles.sider}
    >
      {sidebarContent}
    </Sider>
  );
};
